import {Anchor} from 'src/design/atoms/Anchor';
import {Box} from 'src/design/atoms/Box';
import {Flex} from 'src/design/atoms/Flex';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import React from 'react';
import {Link} from 'react-router-dom';
import {VendorSignupOutcomeEnum} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {ServerErrorAlert} from 'src/app/layout/ServerErrorAlert';
import {ProactForm, ProactFormGrid} from 'src/app/forms/engine/ProactForm';
import {useProactForm} from 'src/app/forms/engine/useProactForm';
import {useAppDispatch, useAppSelector} from 'src/redux/hooks';
import {signupVendorUser} from 'src/redux/slices/vendorSlice';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import type {IFormFieldV2} from 'src/types/FormV2';

interface OutcomeContent {
  lines: string[];
  action?: {label: string; to: string};
}

const getOutcomeContent = (
  outcome: VendorSignupOutcomeEnum | undefined
): OutcomeContent => {
  switch (outcome) {
    case VendorSignupOutcomeEnum.LoginRepaired:
      return {
        lines: [
          'Your account is almost ready. You will receive a temporary signin link to your email within a few minutes.',
          'Please click on the temporary signin link to login for the first time.',
          'Your previous password no longer works — use the new temporary sign-in link instead.',
        ],
      };

    case VendorSignupOutcomeEnum.InviteResent:
      return {
        lines: [
          'A new invite has been sent to your email. You will receive a temporary signin link within a few minutes.',
          'Please click on the temporary signin link to login for the first time.',
        ],
      };

    case VendorSignupOutcomeEnum.AddedWithExistingLogin:
      return {
        lines: [
          'Your existing account has been added to this company. You can sign in now and continue while your access awaits approval.',
        ],
        action: {label: 'Sign in to your account →', to: PathUtility.Login()},
      };

    case VendorSignupOutcomeEnum.AlreadyRegistered:
      return {
        lines: [
          'You already have access to this company. Sign in with your existing credentials to continue.',
        ],
        action: {label: 'Sign in to your account →', to: PathUtility.Login()},
      };

    case VendorSignupOutcomeEnum.PasswordResetRequired:
      return {
        lines: [
          'You already have access to this company. For security, you will need to reset your password before you can sign in.',
        ],
        action: {
          label: 'Reset your password →',
          to: PathUtility.ForgotPassword(),
        },
      };

    case VendorSignupOutcomeEnum.NewLoginCreated:
    default:
      // Also the fallback for an unrecognized outcome.
      return {
        lines: [
          'Your account is almost ready. You will receive a temporary signin link to your email within a few minutes.',
          'Please click on the temporary signin link to login for the first time.',
        ],
      };
  }
};

export interface VendorSignupFormProps {
  code: string;
  email: string;
  isExistingUser?: boolean;
  onRetry: () => void;
}

const formFields: IFormFieldV2[] = [
  {
    name: 'firstName',
    label: 'First Name',
    type: 'text',
    required: true,
    validation: {
      minLength: 1,
      maxLength: 100,
    },
    textboxOptions: {
      validationType: 'name',
    },
  },
  {
    name: 'lastName',
    label: 'Last Name',
    type: 'text',
    required: true,
    validation: {
      minLength: 1,
      maxLength: 100,
    },
    textboxOptions: {
      validationType: 'name',
    },
  },
];

export const VendorSignupForm: React.FC<VendorSignupFormProps> = ({
  code,
  email,
  isExistingUser = false,
}) => {
  const dispatch = useAppDispatch();
  const form = useProactForm({
    formMeta: formFields,
    enablePreSubmissionValidation: false,
  });

  const {
    isLoading: isVendorLoading,
    isError,
    message,
    isSignupCompleted,
    signedupUser,
  } = useAppSelector(state => state.vendor);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (await form.validateForm()) {
      const values = form.getFieldValues();
      void dispatch(
        signupVendorUser({
          code,
          email: email,
          firstName: values.firstName as string,
          lastName: values.lastName as string,
        })
      );
    }
  };

  if (isError && message) {
    return (
      <>
        <ServerErrorAlert message={message} />
        <p>
          Your account was not able to be created. Please contact your
          administrator for support.
        </p>
      </>
    );
  }

  if (isSignupCompleted) {
    // Newtonsoft may send an explicit null where the generated client's
    // type says the field is always present — normalize at the boundary.
    const outcome = signedupUser?.outcome ?? undefined;
    const {lines, action} = getOutcomeContent(outcome);

    return (
      <Stack>
        {lines.map(line => (
          <Text key={line} size="sm" c="dimmed" ta="center">
            {line}
          </Text>
        ))}
        {action && (
          <Anchor size="sm" ta="center" component={Link} to={action.to}>
            {action.label}
          </Anchor>
        )}
      </Stack>
    );
  }

  return (
    <>
      {isExistingUser ? (
        <Text size="sm" c="dimmed" ta="center">
          An account already exists for <strong>{email}</strong>. Continuing
          will add this company to your existing account.
        </Text>
      ) : (
        <Text size="sm" c="dimmed" ta="center">
          To continue, complete the sign up form below for{' '}
          <strong>{email}</strong>.
        </Text>
      )}
      <Box component="form" onSubmit={handleSubmit}>
        <ProactForm form={form}>
          <ProactFormGrid />
        </ProactForm>
        {message && <ServerErrorAlert message={message} />}
        <Flex mt="md" align="center" justify="center">
          <Button
            variant="filled"
            size="lg"
            fullWidth
            type="submit"
            aria-label="sign up button"
            loading={isVendorLoading}>
            {isExistingUser ? 'Add to Company' : 'Sign Up'}
          </Button>
        </Flex>
      </Box>
    </>
  );
};
